// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Ghi hình CỤC BỘ: bản ghi nằm lại trên máy người bấm nút.
//
// Khác hẳn nút "Ghi hình" của host trong bảng điều hành — cái đó gọi API, chạy
// egress phía server, và bản ghi thuộc về workspace. Cái ở đây không chạm tới
// server nào: khung hình đi từ trình duyệt vào MediaRecorder rồi ra một file
// trong thư mục Tải về. Không có hạn mức lưu trữ, không có quét nội dung,
// không có nghĩa vụ xoá — vì không có gì được gửi đi cả.
//
// Hệ quả phải nói thẳng, không phải một thiếu sót đang chờ ai đó vá:
//
//   * **Đóng tab là mất bản ghi.** File chỉ hình thành lúc dừng. Có một lời
//     cảnh báo trước khi rời trang, nhưng trình duyệt cho người dùng bỏ qua nó.
//   * **Không ai khác lấy được bản ghi này**, kể cả quản trị viên. Nó không đi
//     qua Z-Meet.
//   * **Cờ "đang ghi" là một LỜI KHAI, không phải một cơ chế cưỡng chế.** Xem
//     roomdata.ts.
//
// # Vì sao dùng getDisplayMedia thay vì ghép ô lên canvas
//
// Ghép canvas nghe sạch hơn — không hộp thoại nào, không phụ thuộc người dùng
// chọn đúng cửa sổ — nhưng nó có nghĩa là viết lại toàn bộ bố cục phòng họp
// lần thứ hai: lưới, chế độ chỉ-màn-hình, cột thu nhỏ, tên, biểu cảm. Hai bản
// bố cục thì một ngày nào đó chúng lệch nhau, và bản người dùng xem lại sau
// buổi họp là bản không ai từng nhìn thấy lúc họp. Thêm nữa, canvas bị trình
// duyệt bóp khung hình khi tab chạy nền — đúng lúc người ta chuyển sang tab
// khác để ghi chú.
//
// getDisplayMedia ghi đúng thứ người dùng ĐANG NHÌN. Cái giá là một hộp thoại
// chọn nguồn nữa; `preferCurrentTab` của Chromium làm nó thành một cú bấm.
//
// # Vì sao trộn âm thanh bằng tay
//
// Vì "chia sẻ cả tiếng của tab" chỉ có ở Chromium, và ngay ở đó nó bắt người
// dùng tick thêm một ô — bỏ sót là ra một cuốn phim câm mà họ chỉ phát hiện
// sau khi buổi họp tan. Gom thẳng các track âm thanh của LiveKit qua WebAudio
// cho kết quả giống nhau ở mọi trình duyệt và không phụ thuộc vào một ô tick.

import { useCallback, useEffect, useRef, useState } from 'react'
// ⚠️ CHỈ import kiểu. Module này cố tình không kéo theo livekit-client lúc chạy,
// nhờ vậy `npm test` (bộ test có sẵn của Node) import được nó để kiểm phần
// thuần — xem recorder.test.ts.
import type { useRoom } from './livekit'

type RoomHandle = ReturnType<typeof useRoom>

/**
 * Thứ tự thử định dạng.
 *
 * WebM trước vì Chromium và Firefox — hai chỗ có getDisplayMedia — ghi nó tốt
 * nhất. MP4 đứng cuối cho Safari, nơi WebM không có.
 *
 * ⚠️ Cả hai định dạng do MediaRecorder sinh ra đều KHÔNG có tổng thời lượng
 * trong phần đầu file: MediaRecorder ghi một luồng không biết trước khi nào
 * kết thúc. File phát được ở mọi trình duyệt và ở VLC; vài trình phát cũ hiện
 * thời lượng sai hoặc tua vụng. Sửa được bằng cách ghi lại phần đầu file sau
 * khi dừng, và đó là một thư viện chứ không phải mười dòng.
 */
const FORMATS = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
]

/** Đủ rõ cho slide và chữ ở 1080p, chưa tới 1,4 GB cho một giờ họp. */
const VIDEO_BPS = 3_000_000
const AUDIO_BPS = 128_000

/**
 * Cắt khúc mỗi giây.
 *
 * Không phải để ghi dần ra đĩa — cả bộ khúc vẫn nằm chờ tới lúc dừng. Nhưng
 * MediaRecorder trả về Blob, và trình duyệt tự đẩy Blob lớn xuống đĩa thay vì
 * giữ trong RAM; nhiều khúc nhỏ cho nó cơ hội làm điều đó sớm.
 */
const CHUNK_MS = 1000

export type Format = { mime: string; ext: string }

/**
 * pickFormat chọn định dạng đầu tiên trình duyệt ghi được.
 *
 * `mime` rỗng nghĩa là không nhận ra cái nào — khi đó cứ để MediaRecorder tự
 * chọn mặc định của nó, vẫn hơn là từ chối ghi.
 */
export function pickFormat(can: (mime: string) => boolean): Format {
  for (const mime of FORMATS) {
    if (can(mime)) return { mime, ext: mime.startsWith('video/mp4') ? 'mp4' : 'webm' }
  }
  return { mime: '', ext: 'webm' }
}

/**
 * slug đưa tên cuộc họp về ASCII an toàn cho tên file.
 *
 * Bỏ dấu tiếng Việt thay vì giữ nguyên: file này đi qua thư mục Tải về, ổ đĩa
 * mạng, rồi thường là một email đính kèm — và mỗi chặng có một cách khác nhau
 * để làm hỏng tên file Unicode. Chữ "hop-ban-giao" đọc được ở mọi chặng.
 */
function slug(s: string): string {
  const ascii = s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u0111/g, 'd')
    .replace(/\u0110/g, 'D')
  const out = ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 40)
    .replace(/^-+|-+$/g, '')
  return out || 'meeting'
}

/**
 * fileName dựng tên file tải về.
 *
 * Dấu thời gian là giờ ĐỊA PHƯƠNG lúc BẮT ĐẦU ghi, không phải lúc dừng: người
 * dùng nhớ buổi họp bắt đầu lúc nào, chứ không nhớ họ bấm dừng lúc nào.
 */
export function fileName(label: string, at: Date, ext: string): string {
  const p = (n: number) => String(n).padStart(2, '0')
  const stamp =
    `${at.getFullYear()}${p(at.getMonth() + 1)}${p(at.getDate())}` +
    `-${p(at.getHours())}${p(at.getMinutes())}`
  return `zmeet-${slug(label)}-${stamp}.${ext}`
}

/** elapsed định dạng đồng hồ đang chạy: 9:05, và 1:09:05 khi qua một giờ. */
export function elapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const p = (n: number) => String(n).padStart(2, '0')
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h}:${p(m)}:${p(s % 60)}` : `${m}:${p(s % 60)}`
}

/**
 * supported cho biết trình duyệt ghi cục bộ được không.
 *
 * Kiểm TRƯỚC khi hiện nút, cùng lý do với chia sẻ màn hình: Safari trên iOS
 * không có getDisplayMedia và sẽ không có. Hiện nút rồi báo lỗi khi bấm là bắt
 * người dùng tự phát hiện giới hạn của nền tảng thay cho ta.
 */
export function supported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof AudioContext !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  )
}

/** download đẩy blob ra thư mục Tải về của người dùng. */
function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // ⚠️ Thu hồi NGAY lập tức huỷ luôn cú tải vừa bắt đầu ở Firefox và Safari.
  // Một phút là quá đủ để trình duyệt đọc xong blob, và giữ thêm một phút một
  // file đã nằm sẵn trên đĩa không tốn gì.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

type Mixer = {
  ctx: AudioContext
  dest: MediaStreamAudioDestinationNode
  /** track id → nút nguồn đang nối. Khoá theo id để không nối một track hai lần. */
  sources: Map<string, MediaStreamAudioSourceNode>
}

export type LocalRecorder = {
  /** Đang ghi hay không — cũng chính là thứ được loan báo cho cả phòng. */
  recording: boolean
  /** Số giây đã ghi, cập nhật mỗi giây. 0 khi không ghi. */
  seconds: number
  /**
   * start mở hộp thoại chọn nguồn rồi bắt đầu ghi.
   *
   * Người dùng bấm Huỷ ở hộp thoại KHÔNG phải lỗi và không ném gì — cùng quy
   * tắc với chia sẻ màn hình. Mọi thứ khác thì ném, và người gọi phải nói ra:
   * im lặng thất bại ở đây nghĩa là họ tưởng đang ghi suốt cả buổi họp.
   */
  start: () => Promise<void>
  /** stop dừng và lưu file. Gọi nhiều lần vô hại. */
  stop: () => void
}

/**
 * useLocalRecorder ghi buổi họp về máy của chính người dùng.
 *
 * `label` đi vào tên file — tên cuộc họp là lựa chọn đúng.
 */
export function useLocalRecorder(room: RoomHandle, label: string): LocalRecorder {
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [seconds, setSeconds] = useState(0)

  const recRef = useRef<MediaRecorder | null>(null)
  const mixRef = useRef<Mixer | null>(null)
  const chunksRef = useRef<Blob[]>([])
  // label mới nhất, đọc lúc bắt đầu ghi. Không đưa vào dependency của start:
  // đổi tên cuộc họp giữa chừng không được phép dựng lại hàm đang giữ máy ghi.
  const labelRef = useRef(label)
  labelRef.current = label

  // ⚠️ `room` là một object MỚI ở mỗi lần render (useRoom trả về `{...state}`).
  // Đọc nó qua ref để syncAudio giữ nguyên danh tính — nếu không, effect ở dưới
  // chạy lại ở MỌI lần render, kể cả mỗi nhịp đồng hồ của chính máy ghi này.
  const roomRef = useRef(room)
  roomRef.current = room

  /**
   * syncAudio nối MỌI track âm thanh đang sống trong phòng vào bộ trộn.
   *
   * Dựng lại từ trạng thái HIỆN TẠI thay vì vá theo sự kiện — cùng lý do với
   * livekit.snapshot: sự kiện WebRTC đến không theo thứ tự ổn định, và một đồ
   * thị âm thanh được vá dần sẽ lệch theo cách chỉ lộ ra khi nghe lại bản ghi.
   */
  const syncAudio = useCallback(() => {
    const mix = mixRef.current
    const lk = roomRef.current.room
    if (!mix || !lk) return

    const live = new Map<string, MediaStreamTrack>()
    for (const p of [lk.localParticipant, ...lk.remoteParticipants.values()]) {
      for (const pub of p.trackPublications.values()) {
        const t = pub.track?.mediaStreamTrack
        // ⚠️ So `t.kind` của DOM chứ không so Track.Kind của LiveKit — giữ
        // module này không kéo theo livekit-client lúc chạy (xem đầu file).
        if (!t || t.kind !== 'audio' || pub.isMuted || t.readyState !== 'live') continue
        live.set(t.id, t)
      }
    }

    for (const [id, node] of mix.sources) {
      if (live.has(id)) continue
      node.disconnect()
      mix.sources.delete(id)
    }
    for (const [id, track] of live) {
      if (mix.sources.has(id)) continue
      const node = mix.ctx.createMediaStreamSource(new MediaStream([track]))
      node.connect(mix.dest)
      mix.sources.set(id, node)
    }
  }, [])

  // Mốc "phòng vừa đổi" là `room.tiles`: livekit.snapshot dựng lại nó ở đúng
  // những sự kiện làm đồ thị âm thanh cũ đi — người vào, người ra, bật/tắt
  // micro, track được đăng ký. Đăng ký lại chính chuỗi sự kiện đó ở đây là
  // nuôi hai danh sách phải khớp nhau, và một ngày nào đó chúng không khớp.
  useEffect(() => {
    if (startedAt !== null) syncAudio()
  }, [room.tiles, startedAt, syncAudio])

  const stop = useCallback(() => {
    const rec = recRef.current
    if (!rec) return
    recRef.current = null // chặn cú gọi thứ hai trước khi onstop kịp chạy
    if (rec.state === 'inactive') return
    rec.stop() // onstop dọn dẹp và lưu file
  }, [])

  const start = useCallback(async () => {
    if (recRef.current || !roomRef.current.room) return
    if (!supported()) throw new Error('unsupported')

    // Tuỳ chọn riêng của Chromium, gắn ngoài kiểu chuẩn: trình duyệt khác bỏ
    // qua khoá lạ. `preferCurrentTab` để hộp thoại mở sẵn ở tab đang họp —
    // không có nó, người dùng phải tự tìm đúng tab giữa một lưới hình thu nhỏ.
    // `systemAudio: exclude` vì tiếng đã được trộn bằng tay ở dưới; để nó vào
    // là mọi người nói hai lần, lệch nhau một nhịp.
    const opts: DisplayMediaStreamOptions = { video: { frameRate: 30 }, audio: false }
    Object.assign(opts, { preferCurrentTab: true, systemAudio: 'exclude' })

    let display: MediaStream
    try {
      display = await navigator.mediaDevices.getDisplayMedia(opts)
    } catch (e) {
      // NotAllowedError là người dùng bấm Huỷ — không phải lỗi.
      if ((e as DOMException)?.name === 'NotAllowedError') return
      throw e
    }

    try {
      const ctx = new AudioContext()
      const dest = ctx.createMediaStreamDestination()
      mixRef.current = { ctx, dest, sources: new Map() }
      // AudioContext sinh ra ở trạng thái suspended nếu trang chưa có tương
      // tác nào. Ta đang ở trong một cú bấm nên gần như luôn chạy được; không
      // chạy được thì bản ghi mất tiếng chứ không hỏng, nên không ném.
      await ctx.resume().catch(() => {})
      syncAudio()

      const { mime, ext } = pickFormat((m) => MediaRecorder.isTypeSupported(m))
      const at = new Date()
      const name = fileName(labelRef.current, at, ext)

      const stream = new MediaStream([
        ...display.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ])
      const rec = new MediaRecorder(stream, {
        ...(mime ? { mimeType: mime } : {}),
        videoBitsPerSecond: VIDEO_BPS,
        audioBitsPerSecond: AUDIO_BPS,
      })

      chunksRef.current = []
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      rec.onstop = () => {
        for (const t of display.getTracks()) t.stop()

        const mix = mixRef.current
        if (mix) {
          for (const node of mix.sources.values()) node.disconnect()
          mix.sources.clear()
          void mix.ctx.close().catch(() => {})
          mixRef.current = null
        }

        const chunks = chunksRef.current
        chunksRef.current = []
        recRef.current = null
        setStartedAt(null)
        // Không có khúc nào nghĩa là chưa kịp ghi được gì. Tải về một file
        // rỗng là tệ hơn không tải gì: người dùng mở nó ra rồi kết luận tính
        // năng hỏng.
        if (chunks.length > 0) download(new Blob(chunks, { type: mime || 'video/webm' }), name)
      }
      // Máy ghi tự chết (hết đĩa, codec sập) thì vẫn phải cứu phần đã ghi.
      rec.onerror = () => stop()

      // Người dùng bấm "Dừng chia sẻ" ở thanh của trình duyệt — với họ đó là
      // "dừng ghi". Không bắt lấy thì máy ghi tiếp tục chạy trên một track đã
      // chết và sinh ra một file đứng hình.
      display.getVideoTracks()[0]?.addEventListener('ended', () => stop())

      recRef.current = rec
      rec.start(CHUNK_MS)
      setStartedAt(at.getTime())
    } catch (e) {
      // Dựng máy ghi hỏng giữa chừng: trả lại luồng màn hình, nếu không thanh
      // "đang chia sẻ" của trình duyệt còn nằm đó mãi mà không ghi gì.
      for (const t of display.getTracks()) t.stop()
      mixRef.current?.ctx.close().catch(() => {})
      mixRef.current = null
      throw e
    }
  }, [syncAudio, stop])

  // Đồng hồ. Đếm từ mốc bắt đầu chứ không cộng dồn mỗi nhịp: setInterval trôi,
  // và một buổi họp hai tiếng đủ dài để cái trôi đó nhìn thấy được.
  useEffect(() => {
    if (startedAt === null) {
      setSeconds(0)
      return
    }
    const tick = () => setSeconds(Math.floor((Date.now() - startedAt) / 1000))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [startedAt])

  // Rời phòng, đóng màn hình, chuyển trang — dừng và LƯU. Bỏ qua ở đây nghĩa
  // là người dùng mất cả buổi họp vì bấm nhầm nút Rời.
  useEffect(() => () => stop(), [stop])

  // File chỉ hình thành lúc dừng, nên đóng tab giữa chừng là mất trắng. Trình
  // duyệt không cho ta chọn câu chữ của hộp thoại này và cho người dùng bỏ
  // qua nó — nhưng một lời hỏi lại vẫn hơn hẳn im lặng mất một giờ ghi hình.
  useEffect(() => {
    if (startedAt === null) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [startedAt])

  return { recording: startedAt !== null, seconds, start, stop }
}
